import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEligibilityData } from "../useEligibilityData";
import { useStageGuard } from "../useStageGuard";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import Loader from "../../../components/atoms/Loader";
import eligStyles from "../index.module.css";

const formatMoney = (value: any) =>
  value != null ? `₹${Number(value).toLocaleString("en-IN")}` : "—";

const formatPercent = (value: any) => (value != null ? `${value}%` : "—");

// Same formula as the backend KFS PDF (IKeyFactSheetDetails.ts setLoanRequest, non-Personal branch)
const getProcessingFee = (product: any, loanAmount: any) => {
  if (!product || loanAmount == null) return null;
  if (product.processingfeeType === 1) {
    return product.processingfeeValue;
  } else if (product.processingfeeType === 3) {
    if (loanAmount <= product.processingfeeLimit) {
      return product.processingfeeValue;
    } else {
      return product.processingfeeValue * loanAmount;
    }
  } else {
    return product.processingfeeValue * loanAmount;
  }
};

function EligibilityKFS() {
  const navigate = useNavigate();
  const userData = useEligibilityData();

  useStageGuard(userData?.userId, userData?.applicationId, 8);

  const urlData = JSON.parse(sessionStorage.getItem("eligibility_url_data") || "{}");

  const [sanctionData, setSanctionData] = useState<any>(null);
  const [productData, setProductData] = useState<any>(null);
  const [loanTenure, setLoanTenure] = useState<any>(null);
  const [firstEmi, setFirstEmi] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    const saved = JSON.parse(sessionStorage.getItem("eligibility_data") || "{}");
    const appId = userData?.applicationId || saved?.applicationId;
    const userId = userData?.userId || saved?.userId;
    if (!appId) {
      setError("Unable to load Key Fact Statement.");
      return;
    }

    const token = sessionStorage.getItem("eligibility_auth_token");
    const headers = { Authorization: `Bearer ${token}` };

    const load = async () => {
      setLoading(true);
      try {
        const sanctionRes = await fetch(
          `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/sanction-details?applicationId=${appId}`,
          { headers }
        );
        const sanctionJson = await sanctionRes.json();
        if (!sanctionJson?.data) throw new Error("No sanction details");
        setSanctionData(sanctionJson.data);

        // Processing fee settings (loan_products)
        try {
          const productRes = await fetch(
            `${process.env.REACT_APP_DASHBOARD_URL}/products/id?productId=${sanctionJson.data.productId}`,
            { headers }
          );
          const productJson = await productRes.json();
          setProductData(productJson?.data || null);
        } catch {
          // fee row falls back to "—"
        }

        // 1st EMI (emi_chart, saved at approval)
        try {
          const scheduleRes = await fetch(
            `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/emi-schedule?applicationId=${appId}`,
            { headers }
          );
          const scheduleJson = await scheduleRes.json();
          const list: any[] = Array.isArray(scheduleJson?.data) ? scheduleJson.data : [];
          setFirstEmi(list.find((e) => e.emiNo === 1) || null);
        } catch {
          // row falls back to "—"
        }

        // Tenure (applications.loan_tenure)
        if (userId) {
          try {
            const summaryRes = await fetch(
              `${process.env.REACT_APP_DASHBOARD_URL}/summary/user?id=${userId}&applicationId=${appId}`,
              { headers }
            );
            const summaryJson = await summaryRes.json();
            setLoanTenure(summaryJson?.data?.loanTenure ?? null);
          } catch {
            // tenure falls back to link / EMI count
          }
        }
      } catch {
        setError("Unable to load Key Fact Statement.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userData?.applicationId, userData?.userId]);

  const totalInterest = sanctionData?.totalInterest != null
    ? sanctionData.totalInterest
    : sanctionData?.totalInstallment != null && sanctionData?.totalPrincipal != null
      ? Math.max(0, sanctionData.totalInstallment - sanctionData.totalPrincipal)
      : null;

  const tenure = loanTenure
    || urlData?.loanTenure
    || (sanctionData?.totalInstallment && sanctionData?.emi
      ? Math.round(sanctionData.totalInstallment / sanctionData.emi)
      : null);

  const processingFee = sanctionData?.pfToBePaid ?? getProcessingFee(productData, sanctionData?.loanAmount);

  const rows: { label: string; value: string }[] = [
    { label: "Loan amount (sanctioned)", value: formatMoney(sanctionData?.loanAmount) },
    { label: "Net disbursed amount", value: formatMoney(sanctionData?.netDisbursement) },
    { label: "Total interest payable", value: formatMoney(totalInterest) },
    { label: "Interest rate (p.a.)", value: formatPercent(sanctionData?.roi) },
    { label: "APR", value: formatPercent(sanctionData?.aprIrr) },
    { label: "Tenure", value: tenure ? `${tenure} months` : "—" },
    { label: "EMI", value: formatMoney(sanctionData?.emi) },
    {
      label: "Processing fee + GST",
      value: processingFee != null
        ? `₹${Math.ceil(processingFee).toLocaleString("en-IN")} + ₹${Math.ceil(processingFee * 0.18).toLocaleString("en-IN")}`
        : "—",
    },
    ...(sanctionData?.pfActual != null
      ? [{ label: "Processing fee (actual)", value: formatMoney(sanctionData.pfActual) }]
      : []),
    ...(sanctionData?.discountOnPf != null
      ? [{ label: "Discount on processing fee", value: formatMoney(sanctionData.discountOnPf) }]
      : []),
    { label: "Total amount payable", value: formatMoney(sanctionData?.totalInstallment) },
    { label: "1st EMI amount", value: formatMoney(firstEmi?.emiAmount) },
  ];

  return ( 
    <div className={eligStyles.body}>
      <div className={eligStyles.backdrop}>
        <Navbar />
        <div className={eligStyles.main}>
          <div className={eligStyles.cardWrapper}>
            <div className={eligStyles.container}>
              <div className={eligStyles.cardHeader}>
                <div style={{
                  width: "2.4rem", height: "2.4rem", borderRadius: "50%",
                  background: "#F4E5DD", display: "flex", alignItems: "center",
                  justifyContent: "center", flexShrink: 0,
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M14 2H6C4.895 2 4 2.895 4 4V20C4 21.105 4.895 22 6 22H18C19.105 22 20 21.105 20 20V8L14 2Z" fill="#d32028" opacity="0.15" stroke="#d32028" strokeWidth="1.5" strokeLinejoin="round" />
                    <polyline points="14,2 14,8 20,8" stroke="#d32028" strokeWidth="1.5" strokeLinejoin="round" />
                    <line x1="8" y1="13" x2="16" y2="13" stroke="#d32028" strokeWidth="1.5" strokeLinecap="round" />
                    <line x1="8" y1="17" x2="13" y2="17" stroke="#d32028" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <p className={eligStyles.cardTitle}>Key Fact Statement</p>
              </div>

              {loading && <Loader />}

              {!loading && sanctionData && (
                <>
                  {/* ── KFS table ── */}
                  <div style={{ border: "1px solid #ececec", borderRadius: "12px", overflow: "hidden" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", padding: "0.7rem 0.9rem", borderBottom: "1px solid #f0f0f0", background: "#fdecea" }}>
                      <span style={{ fontSize: "0.78rem", color: "#d32028", fontWeight: 800, flex: 1 }}>Key Facts</span>
                      <span style={{ fontSize: "0.8rem", color: "#1a1a2e", fontWeight: 700, textAlign: "right", whiteSpace: "nowrap" }}>Details</span>
                    </div>
                    {rows.map((row, index) => (
                      <div
                        key={row.label}
                        style={{
                          display: "flex", justifyContent: "space-between", gap: "0.75rem",
                          padding: "0.7rem 0.9rem",
                          borderBottom: index === rows.length - 1 ? "none" : "1px solid #f0f0f0",
                          background: index % 2 === 1 ? "#fcfbfb" : "#fff",
                        }}
                      >
                        <span style={{ fontSize: "0.78rem", color: "#555", flex: 1 }}>{row.label}</span>
                        <span style={{ fontSize: "0.8rem", color: "#1a1a2e", fontWeight: 700, textAlign: "right", whiteSpace: "nowrap" }}>{row.value}</span>
                      </div>
                    ))}
                  </div>

                  {/* ── Consent ── */}
                  <label style={{ position: "relative", display: "flex", alignItems: "flex-start", gap: "0.55rem", background: "#fdecea", border: "1px solid #f6cfcc", borderRadius: "10px", padding: "0.7rem 0.85rem", width: "100%", margin: "0.8rem 0 0", boxSizing: "border-box", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => setIsChecked(e.target.checked)}
                      style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{ width: "18px", height: "18px", borderRadius: "5px", border: "2px solid #d32028", background: isChecked ? "#d32028" : "transparent", flexShrink: 0, marginTop: "1px", display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box" }}>
                      {isChecked && (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </span>
                    <span style={{ display: "block", textAlign: "left", fontSize: "0.72rem", color: "#555", lineHeight: 1.5 }}>
                      I have read and understood the <b>Key Fact Statement (KFS)</b> and accept the loan terms, charges and APR disclosed above.
                    </span>
                  </label>
                </>
              )}

              {!loading && error && (
                <p style={{ color: "#d32028", fontSize: "0.85rem", marginTop: "0.75rem" }}>{error}</p>
              )}
            </div>

            {/* ── Accept & Continue ── */}
            <div style={{ padding: "0rem 1.25rem 1rem", display: "flex", flexDirection: "row", alignItems: "center", gap: "0.6rem", justifyContent: "center" }}>
              <button
                // onClick={() => navigate("/eligibility/sanction-letter", { state: { data: userData } })}
                onClick={() => navigate("/eligibility/repayment-schedule", { state: { data: userData } })}
                disabled={!isChecked}
                style={{
                  width: "70%",
                  padding: "0.85rem 1.5rem",
                  background: isChecked ? "#d32028" : "#c1c1c1",
                  color: "#fff",
                  border: "none",
                  borderRadius: "60px",
                  fontSize: "0.95rem",
                  fontWeight: 700,
                  fontFamily: "Outfit, sans-serif",
                  cursor: isChecked ? "pointer" : "not-allowed",
                  letterSpacing: "0.3px",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                Accept & Continue
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M13 6L19 12L13 18" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {process.env.REACT_APP_CURRENT !== 'prod' && (
                <button
                  onClick={() => navigate("/eligibility/repayment-schedule", { state: { data: userData } })}
                  style={{ background: "none", border: "none", color: "#bbb", fontSize: "0.78rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline" }}
                >
                  Skip
                </button>
              )}
            </div>
            <Footer light />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EligibilityKFS;
